/** 首頁：本月小計 ＋ 兩個大掣（影相記帳／記收入）＋ 最近紀錄 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, currentPeriod, fmtMoney, type Me, type Meta, type VoucherRow } from '../lib/api';

export default function Home({ meta, me, onLogout }: { meta: Meta; me: Me; onLogout: () => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<VoucherRow[]>([]);
  const [totals, setTotals] = useState<{ kind: string; total_cents: number; n: number }[]>([]);
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.vouchers({ period: currentPeriod(), entityId })
      .then((r) => { setRows(r.vouchers.slice(0, 10)); setTotals(r.totals); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId]);

  const total = (kind: string) => totals.find((x) => x.kind === kind)?.total_cents ?? 0;
  const pending = rows.filter((r) => r.reimbursement_status === 'pending');
  const net = total('receipt') - total('payment');

  return (
    <main className="page">
      <header className="topbar">
        <h1>{t('app.title')}</h1>
        <span className="who">{me.name} <button type="button" onClick={onLogout}>{t('auth.sign_out')}</button></span>
      </header>

      {/* 大數字（本月）*/}
      <section className="card">
        <label className="field">
          <span>{t('home.entity')}</span>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
          </select>
        </label>
        <div className="dash">
          <div className="dash-item"><small>{t('home.expense')}</small><b className="neg">{fmtMoney(total('payment'))}</b></div>
          <div className="dash-item"><small>{t('home.income')}</small><b className="pos">{fmtMoney(total('receipt'))}</b></div>
          <div className="dash-item"><small>{t('home.net')}</small><b>{fmtMoney(net)}</b></div>
        </div>
        <p className="note">{t('home.period', { period: currentPeriod() })}</p>
      </section>

      {/* 快速動作 */}
      <div className="row">
        <Link className="cta" to="/capture">📷 {t('home.capture_cta')}</Link>
        <Link className="cta secondary" to="/income">💰 {t('home.income_cta')}</Link>
      </div>

      {/* 待辦 */}
      <section className="card">
        <h2>{t('home.todo')}</h2>
        {pending.length > 0
          ? <p className="note"><Link to="/vouchers">{t('home.pending_reimb', { n: pending.length })}</Link></p>
          : <p className="note">{t('home.todo_none')}</p>}
      </section>

      {err && <p className="err">{err}</p>}

      {/* 最近紀錄 */}
      <section className="card">
        <h2>{t('home.recent')}</h2>
        {rows.length === 0 && <p className="note">{t('list.empty')}</p>}
        <ul className="list">
          {rows.map((v) => (
            <li key={v.id}>
              <span className="no">{v.voucher_no}</span>
              <span className="purpose">{v.purpose}</span>
              <span className={v.kind === 'receipt' ? 'pos' : 'neg'}>
                {v.kind === 'receipt' ? '+' : '−'}{fmtMoney(v.amount_cents)}
              </span>
              <span className="tag">{v.paid_by === 'company' ? t('expense.paid_by_company') : t(`expense.paid_by_${v.paid_by}`)}</span>
              {v.attachments > 0 && <span className="tag">📎{v.attachments}</span>}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
